import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAuthState, useAuthActions } from './AuthContext';
import logger from '@/lib/utils/logger';

interface GlobalWebSocketContextType {
  isConnected: boolean;
  subscribe: (handler: (data: any) => void) => () => void;
  subscribeToSession: (sessionId: string, handler: (data: any) => void) => () => void;
  sendMessage: (message: any) => void;
  joinSession: (sessionId: string) => void;
  leaveSession: (sessionId: string) => void;
  isSessionJoined: (sessionId: string) => boolean;
}

const GlobalWebSocketContext = createContext<GlobalWebSocketContextType | undefined>(undefined);

export const useGlobalWebSocket = () => {
  const context = useContext(GlobalWebSocketContext);
  if (!context) {
    // Dev-Guard: vermeidet HMR-Crashes, wenn Provider-Boundary kurzzeitig inkonsistent ist
    if (import.meta.env.DEV) {
      return {
        isConnected: false,
        subscribe: () => { return () => { return; }; },
        subscribeToSession: () => { return () => { return; }; },
        sendMessage: () => { return; },
        joinSession: () => { return; },
        leaveSession: () => { return; },
        isSessionJoined: () => false,
      } as GlobalWebSocketContextType;
    }
    throw new Error('useGlobalWebSocket must be used within a GlobalWebSocketProvider');
  }
  return context;
};

interface GlobalWebSocketProviderProps {
  children: ReactNode;
}

export const GlobalWebSocketProvider: React.FC<GlobalWebSocketProviderProps> = ({ children }) => {
  const { token } = useAuthState();
  const { logout, refreshTokenOnActivity } = useAuthActions();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<(data: any) => void>>(new Set());
  const sessionHandlersRef = useRef<Map<string, Set<(data: any) => void>>>(new Map());
  const joinedSessionsRef = useRef<Set<string>>(new Set());
  const reconnectTimeoutRef = useRef<number>();
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const triedAltUrlRef = useRef(false);
  const pendingMessagesRef = useRef<any[]>([]);
  const lastWarnAtRef = useRef(0);

  // Pong-Timeout-Detection
  const lastPongTimeRef = useRef<number>(Date.now());
  const PONG_TIMEOUT_MS = 25000; // 25 Sekunden
  
  // Performance optimizations
  const messageDeduplicationRef = useRef<Set<string>>(new Set());
  const lastMessageTimestampRef = useRef<Map<string, number>>(new Map());
  const messageBatchRef = useRef<any[]>([]);
  const batchTimeoutRef = useRef<number>();

  // Message deduplication helper
  const isDuplicateMessage = useCallback((data: any): boolean => {
    const messageId = `${data.type}-${data.session_id || 'global'}-${data.character_id || ''}-${data.timestamp || Date.now()}`;
    const lastTimestamp = lastMessageTimestampRef.current.get(messageId);
    const currentTime = Date.now();
    
    // Consider duplicate if same message type within 100ms
    if (lastTimestamp && currentTime - lastTimestamp < 100) {
      return true;
    }
    
    lastMessageTimestampRef.current.set(messageId, currentTime);
    
    // Clean old timestamps (older than 5 seconds)
    for (const [key, timestamp] of lastMessageTimestampRef.current.entries()) {
      if (currentTime - timestamp > 5000) {
        lastMessageTimestampRef.current.delete(key);
      }
    }
    
    return false;
  }, []);

  // Message batching helper
  const processBatchedMessages = useCallback(() => {
    if (messageBatchRef.current.length === 0) return;
    
    const batch = [...messageBatchRef.current];
    messageBatchRef.current = [];
    
    batch.forEach(data => {
      // Route session-specific messages first
      if (data.session_id && sessionHandlersRef.current.has(data.session_id)) {
        const sessionHandlers = sessionHandlersRef.current.get(data.session_id);
        if (sessionHandlers && sessionHandlers.size > 0) {
          sessionHandlers.forEach(handler => {
            try {
              if (typeof handler === 'function') {
                handler(data);
              }
            } catch (error) {
              console.error('Error in session WebSocket handler:', error);
            }
          });
        }
      }
      
      // Always notify global subscribers for important events
      const shouldNotifyGlobal = !data.session_id ||
        data.type === 'character_status_update' ||
        data.type === 'typing' ||
        data.type === 'chat_status' ||
        data.type === 'character_typing' ||
        data.type === 'unread_count_update' ||
        data.type === 'balance_update' ||
        data.type === 'blur_status_changed' ||
        data.type === 'ai_bulk_status' ||
        data.type === 'ai_job_status' ||
        data.type === 'gift_image_job_status' ||
        data.type === 'ai_response' ||
        data.type === 'message' ||
        data.type === 'chat_message' ||
        data.type === 'message_saved' ||
        data.type === 'notification' ||
        data.type === 'notification_count_update' ||
        data.type === 'new_match' ||
        data.type === 'profile_visit';
        
      if (shouldNotifyGlobal) {
        handlersRef.current.forEach(handler => {
          try {
            if (typeof handler === 'function') {
              handler(data);
            }
          } catch (error) {
            console.error('Error in global WebSocket handler:', error);
          }
        });
      }
    });
  }, []);

  const connect = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current) return;
    
    isConnectingRef.current = true;

    // Dynamische WebSocket-URL - in Prod strikt Same-Origin, in Dev Env-Override erlaubt
    const WS_BASE_URL = (() => {
      const isLocalhost = (() => {
        try {
          return (
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'
          );
        } catch {
          return false;
        }
      })();

      if (isLocalhost && import.meta.env.VITE_WS_BASE_URL) {
        return import.meta.env.VITE_WS_BASE_URL as string;
      }
      return window.location.origin.replace(/^http/, 'ws');
    })();
    // Auth via query param to avoid cookie issues through dev proxy
    const shouldSendTokenQuery = typeof token === 'string' && token.trim().length > 0 && token !== 'cookie';
    const authQuery = shouldSendTokenQuery ? `?token=${encodeURIComponent(token)}` : '';
    const wsUrl = `${WS_BASE_URL}/api/v1/websocket/ws${authQuery}`;
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔗 WebSocket connecting to:', wsUrl);
    }
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    // Keep-alive ping interval
    let pingInterval: ReturnType<typeof setInterval>;

    ws.onopen = () => {
      // console.log('✅ WebSocket CONNECTED');
      setIsConnected(true);
      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0;

      // Join any sessions that were queued while connecting
      joinedSessionsRef.current.forEach(sessionId => {
        try {
          ws.send(JSON.stringify({ type: 'join_session', session_id: sessionId }));
          if (process.env.NODE_ENV === 'development') {
            // console.log('🔗 Joining queued session:', sessionId);
          }
        } catch (error) {
          console.error('Failed to join queued session:', sessionId, error);
        }
      });

      // Reset pong time on new connection
      lastPongTimeRef.current = Date.now();

      // Start keep-alive ping every 20 seconds with pong-timeout detection
      pingInterval = setInterval(() => {
        // Early exit if socket is not open - prevents "already CLOSING" errors
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          clearInterval(pingInterval);
          return;
        }

        const timeSinceLastPong = Date.now() - lastPongTimeRef.current;

        // Wenn kein Pong seit 25s → Connection ist tot!
        if (timeSinceLastPong > PONG_TIMEOUT_MS) {
          clearInterval(pingInterval);
          logger.warn(`WebSocket dead: No pong for ${timeSinceLastPong}ms, forcing reconnect`);
          ws.close(4000, 'Pong timeout');
          return;
        }

        // Ping senden
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          clearInterval(pingInterval);
          logger.warn('Ping failed, forcing reconnect');
          ws.close(4001, 'Ping failed');
        }
      }, 20000);

      // Flush queued messages
      try {
        while (pendingMessagesRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
          const next = pendingMessagesRef.current.shift();
          if (next) {
            ws.send(JSON.stringify(next));
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to flush queued WebSocket messages:', error);
        }
      }
    };

    ws.onmessage = (event) => {
      // Early exit if socket is closing - prevents "already CLOSING" errors
      if (ws.readyState !== WebSocket.OPEN) {
        return;
      }

      try {
        const data = JSON.parse(event.data);

        // Guard against null/undefined data (e.g., server sends "null")
        if (!data || typeof data !== 'object') {
          return;
        }

        // DEBUG: Log all incoming WebSocket messages
        if (data.type === 'message' || data.type === 'chat_message' || data.type === 'ai_response') {
          logger.debug('[WS] Received message event:', {
            type: data.type,
            session_id: data.session_id,
            has_content: !!data.content,
            is_from_character: data.is_from_character,
            subscribed_sessions: Array.from(sessionHandlersRef.current.keys()),
            session_id_type: typeof data.session_id
          });
        }

        // Handle pong responses (keep-alive) - update lastPongTime
        if (data.type === 'pong') {
          lastPongTimeRef.current = Date.now();
          return;
        }

        // Handle server pings - respond with pong (only if socket still open)
        if (data.type === 'server_ping') {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'server_pong' }));
            } catch (e) {
              // Ignore - connection probably dead
            }
          }
          return;
        }
        
        // Skip duplicate messages - but NEVER for notification events or typing
        // Notifications have unique IDs and should always be processed
        // Typing events need real-time updates
        // message_saved must always be processed - one per user message
        const skipDeduplication =
          data.type === 'notification' ||
          data.type === 'notification_count_update' ||
          data.type === 'new_match' ||
          data.type === 'profile_visit' ||
          data.type === 'typing' ||
          data.type === 'chat_status' ||
          data.type === 'message_saved';

        if (!skipDeduplication && isDuplicateMessage(data)) {
          return;
        }
        
        // Trigger activity-based token refresh nur wenn Tab sichtbar & fokussiert
        if (
          (data.type === 'message' || data.type === 'chat_message' || data.type === 'ai_response' || data.type === 'typing') &&
          typeof document !== 'undefined' && document.visibilityState === 'visible'
        ) {
          try {
            if (document.hasFocus && document.hasFocus()) {
              void refreshTokenOnActivity();
            }
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Token refresh in WebSocket failed, but continuing:', error);
            }
          }
        }
        
        // Debug critical WebSocket events
        if (data.type === 'typing' || data.type === 'message' || data.type === 'chat_message' || data.type === 'message_seen' || data.type === 'ai_bulk_status' || data.type === 'connection_established') {
          // console.log(`🔵 WS [${data.type}]:`, data);
        } else if (data.type === 'error') {
          console.error('❌ WS Error:', data);
        }
        
        // IMMEDIATE processing for time-critical events
        // These should NOT be batched to ensure instant UI updates
        const isImmediateEvent =
          data.type === 'notification' ||
          data.type === 'notification_count_update' ||
          data.type === 'new_match' ||
          data.type === 'profile_visit' ||
          data.type === 'balance_update' ||
          data.type === 'blur_status_changed' ||
          data.type === 'message_saved' ||  // User message confirmation - must be instant
          data.type === 'typing' ||
          data.type === 'chat_status' ||
          data.type === 'ai_response' ||
          data.type === 'chat_message' ||
          data.type === 'message' ||
          data.type === 'ai_bulk_status' ||  // Live preview updates - must be instant
          data.type === 'ai_job_status' ||   // Job status updates - must be instant
          data.type === 'gift_image_job_status';  // Admin gift image generation progress
          
        if (isImmediateEvent) {
          // Process immediately without batching
          // First, route to session-specific handlers if applicable
          if (data.session_id && sessionHandlersRef.current.has(data.session_id)) {
            const sessionHandlers = sessionHandlersRef.current.get(data.session_id);
            // DEBUG: Log session handler routing
            logger.debug('[WS] Routing to session handlers:', {
              session_id: data.session_id,
              handler_count: sessionHandlers?.size || 0
            });
            if (sessionHandlers && sessionHandlers.size > 0) {
              sessionHandlers.forEach(handler => {
                try {
                  if (typeof handler === 'function') {
                    handler(data);
                  }
                } catch (error) {
                  console.error('Error in immediate session WebSocket handler:', error);
                }
              });
            }
          } else if (data.session_id) {
            // DEBUG: Log when session_id exists but no handlers found
            logger.debug('[WS] Session ID present but no handlers:', {
              incoming_session_id: data.session_id,
              registered_sessions: Array.from(sessionHandlersRef.current.keys())
            });
          }
          // Also notify global handlers
          handlersRef.current.forEach(handler => {
            try {
              if (typeof handler === 'function') {
                handler(data);
              }
            } catch (error) {
              console.error('Error in immediate WebSocket handler:', error);
            }
          });
          return; // Don't add to batch
        }
        
        // Add message to batch for processing (non-critical events)
        messageBatchRef.current.push(data);

        // Clear existing batch timeout
        if (batchTimeoutRef.current) {
          clearTimeout(batchTimeoutRef.current);
        }

        // Process batch after 50ms or when batch size reaches 10
        // Längeres Interval für besseres Batching bei Bulk-Messages
        const BATCH_SIZE_THRESHOLD = 10;
        const BATCH_TIMEOUT_MS = 50;

        if (messageBatchRef.current.length >= BATCH_SIZE_THRESHOLD) {
          processBatchedMessages();
        } else {
          batchTimeoutRef.current = window.setTimeout(() => {
            processBatchedMessages();
          }, BATCH_TIMEOUT_MS);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('WebSocket error:', { error, readyState: ws.readyState, url: ws.url });
      }
    };

    ws.onclose = (event) => {
      if (process.env.NODE_ENV === 'development') {
        // console.log(`🔴 WebSocket disconnected - Code: ${event.code}, Reason: ${event.reason || 'No reason'}`);
      }
      setIsConnected(false);
      isConnectingRef.current = false;
      wsRef.current = null;

      // Clear ping interval
      if (pingInterval) {
        clearInterval(pingInterval);
      }

      // Handle different close codes
      if (event.code === 4001) {
        console.error('Global WebSocket: Invalid token - authentication failed');
        logout();
        return;
      }

      // Check for authentication errors
      if (event.code === 1008) {
        // console.log('Global WebSocket authentication failed - token expired or invalid');
        logout();
        return;
      }

      // One-time alternate URL fallback in DEV if configured URL fails (helps when backend runs on same origin)
      if (import.meta.env.DEV && !triedAltUrlRef.current) {
        const altUrl = window.location.origin.replace(/^http/, 'ws') + `/api/v1/websocket/ws`;
        triedAltUrlRef.current = true;
        try {
          const alt = new WebSocket(altUrl);
          alt.onopen = () => {
            // Switch to alt connection
            wsRef.current = alt;
            setIsConnected(true);
            isConnectingRef.current = false;
            reconnectAttemptsRef.current = 0;
          };
          alt.onmessage = ws.onmessage;
          alt.onerror = ws.onerror;
          alt.onclose = ws.onclose;
          return;
        } catch (_) {
          // ignore and proceed to normal reconnect
        }
      }

      // Auth-Fehler → logout, kein Reconnect (bereits oben behandelt für 4001 und 1008)
      // IMMER reconnecten (kein Limit mehr!) - außer bei Auth-Fehlern
      if (token) {
        reconnectAttemptsRef.current++;
        // Exponential backoff bis max 30 Sekunden, sanfterer Anstieg mit 1.5
        const timeout = Math.min(1000 * Math.pow(1.5, Math.min(reconnectAttemptsRef.current, 10)), 30000);
        console.log(`WebSocket: Reconnecting in ${Math.round(timeout / 1000)}s (attempt ${reconnectAttemptsRef.current})`);
        reconnectTimeoutRef.current = window.setTimeout(connect, timeout);
      }
    };
  }, [token, logout, refreshTokenOnActivity, isDuplicateMessage, processBatchedMessages]);

  useEffect(() => {
    // Add small delay to ensure auth context is fully initialized after login
    if (token) {
      const connectTimeout = setTimeout(() => {
        connect();
      }, 100); // 100ms delay to avoid race condition with auth initialization
      
      return () => clearTimeout(connectTimeout);
    }

    return () => {
      reconnectAttemptsRef.current = 10; // Prevent reconnection on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [connect, token]);

  const subscribe = useCallback((handler: (data: any) => void) => {
    handlersRef.current.add(handler);
    
    // Return unsubscribe function
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const subscribeToSession = useCallback((sessionId: string, handler: (data: any) => void) => {
    logger.debug('[WS] Subscribing to session:', sessionId);
    if (!sessionHandlersRef.current.has(sessionId)) {
      sessionHandlersRef.current.set(sessionId, new Set());
    }
    sessionHandlersRef.current.get(sessionId)!.add(handler);

    // Return unsubscribe function
    return () => {
      logger.debug('[WS] Unsubscribing from session:', sessionId);
      const handlers = sessionHandlersRef.current.get(sessionId);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          sessionHandlersRef.current.delete(sessionId);
        }
      }
    };
  }, []);

  const sendMessage = useCallback((data: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(data));
        if (data.type === 'chat_message' && process.env.NODE_ENV === 'development') {
          // console.log('📤 WS Sent:', data.type);
        }
      } catch (error) {
        console.error('WebSocket send failed, forcing reconnect:', error);
        // KRITISCH: Bei Send-Fehler sofort reconnecten!
        ws.close(4002, 'Send failed');
        // Message queuen für Retry nach Reconnect
        pendingMessagesRef.current.push(data);
      }
      return;
    }

    // Queue message for later and rate-limit warnings in DEV
    pendingMessagesRef.current.push(data);
    if (pendingMessagesRef.current.length > 50) {
      pendingMessagesRef.current.shift();
    }
    if (process.env.NODE_ENV === 'development') {
      const now = Date.now();
      if (now - lastWarnAtRef.current > 5000) {
        console.warn('WebSocket not connected; message queued. Pending:', pendingMessagesRef.current.length);
        lastWarnAtRef.current = now;
      }
    }
  }, []);

  const joinSession = useCallback((sessionId: string) => {
    if (!joinedSessionsRef.current.has(sessionId)) {
      joinedSessionsRef.current.add(sessionId);
      
      // If WebSocket is connected, join immediately
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'join_session', session_id: sessionId });
        if (process.env.NODE_ENV === 'development') {
          // console.log('Joined session via GlobalWebSocket:', sessionId);
        }
      } else {
        // Otherwise, it will be joined when connection opens
        if (process.env.NODE_ENV === 'development') {
          // console.log('Queueing session join for when WebSocket connects:', sessionId);
        }
      }
    }
  }, [sendMessage]);

  const leaveSession = useCallback((sessionId: string) => {
    if (joinedSessionsRef.current.has(sessionId)) {
      joinedSessionsRef.current.delete(sessionId);
      sendMessage({ type: 'leave_session', session_id: sessionId });
      if (process.env.NODE_ENV === 'development') {
        // console.log('Left session via GlobalWebSocket:', sessionId);
      }
    }
  }, [sendMessage]);

  const isSessionJoined = useCallback((sessionId: string) => {
    return joinedSessionsRef.current.has(sessionId);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<GlobalWebSocketContextType>(() => ({
    isConnected,
    subscribe,
    subscribeToSession,
    sendMessage,
    joinSession,
    leaveSession,
    isSessionJoined
  }), [isConnected, subscribe, subscribeToSession, sendMessage, joinSession, leaveSession, isSessionJoined]);

  return (
    <GlobalWebSocketContext.Provider value={value}>
      {children}
    </GlobalWebSocketContext.Provider>
  );
};